import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  message,
  Popconfirm,
  Alert,
  Switch,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  DownOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';

const { Title } = Typography;
const { Option } = Select;

const Categories = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [selectedParent, setSelectedParent] = useState(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // Clear any cached data on component mount
  useEffect(() => {
    queryClient.removeQueries('categories');
  }, [queryClient]);

  // Fetch categories
  const {
    data: categories = [],
    isLoading,
    error,
    refetch,
  } = useQuery(
    'categories',
    async () => {
      try {
        const response = await api.get('/admin/categories/all');
        const data = response.data?.data || response.data || [];

        // Clear any cached data and use only fresh API data
        const validCategories = data.filter((cat) => cat.id && cat.name);

        return validCategories;
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        if (error.response?.status === 401) {
          window.location.href = '/login';
        }
        throw error;
      }
    },
    {
      refetchInterval: 300000, // Refresh every 5 minutes
      staleTime: 0, // Always consider data stale to force fresh fetch
      cacheTime: 0, // Don't cache data
    },
  );

  // Create category mutation
  const createMutation = useMutation(
    async (values) => {
      const response = await api.post('/admin/categories/create', values);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Category created successfully');
        setIsModalVisible(false);
        form.resetFields();
        queryClient.invalidateQueries('categories');
      },
      onError: (error) => {
        message.error(
          'Failed to create category: ' + (error.response?.data?.message || error.message),
        );
      },
    },
  );

  // Update category mutation
  const updateMutation = useMutation(
    async ({ id, data }) => {
      try {
        const response = await api.put(`/admin/categories/${id}`, data);

        // Check if the backend actually updated the data
        if (response.data) {
          const updatedData = response.data.data || response.data; // Handle both response structures

          if (updatedData.parent_id !== (data?.parent_id ?? null)) {
          }
        }

        return response.data;
      } catch (error) {
        console.error('Update failed:', error);
        throw error;
      }
    },
    {
      onSuccess: (_data, _variables) => {
        message.success('Category updated successfully');
        setIsModalVisible(false);
        setEditingCategory(null);
        setSelectedParent(null);
        form.resetFields();

        // Force refresh the categories data
        setTimeout(() => {
          refetch();
        }, 500);
      },
      onError: (error) => {
        console.error('Update failed:', error);
        message.error('Failed to update category');
      },
    },
  );

  // Delete category mutation
  const deleteMutation = useMutation(
    async (id) => {
      await api.delete(`/admin/categories/${id}`);
    },
    {
      onSuccess: () => {
        message.success('Category deleted successfully');
        queryClient.invalidateQueries('categories');
      },
      onError: (error) => {
        message.error(
          'Failed to delete category: ' + (error.response?.data?.message || error.message),
        );
      },
    },
  );

  // Toggle status mutation
  const toggleStatusMutation = useMutation(
    async ({ id, isActive }) => {
      const response = await api.put(`/admin/categories/${id}`, { is_active: isActive });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Category status updated successfully');
        queryClient.invalidateQueries('categories');
      },
      onError: (error) => {
        message.error(
          'Failed to update category status: ' + (error.response?.data?.message || error.message),
        );
      },
    },
  );

  const handleEdit = (record) => {
    // Allow editing even if category not in current data (might be filtered by backend)
    // The backend will handle the update properly
    setEditingCategory(record);

    // Find the actual parent category from the full categories array
    if (record.parent_id) {
      const parent = categories.find((cat) => cat.id === record.parent_id);
      setSelectedParent(parent ? parent.id : null);
      form.setFieldsValue({
        ...record,
        parent_id: parent ? parent.id : null,
      });
    } else {
      setSelectedParent(null);
      form.setFieldsValue(record);
    }
    setIsModalVisible(true);
  };

  const handleAddSubCategory = (parentCategory) => {
    setSelectedParent(parentCategory.id);
    setEditingCategory(null);
    form.setFieldsValue({
      parent_id: parentCategory.id,
      type: parentCategory.type || 'product',
    });
    setIsModalVisible(true);
  };

  const handleToggleStatus = (id, isActive) => {
    toggleStatusMutation.mutate({ id, isActive });
  };

  const handleSubmit = async (values) => {
    try {
      // Handle parent_id - ensure null is sent when cleared
      const submitData = {
        ...values,
        parent_id: values.parent_id || null,
      };

      // Ensure parent_id is null for empty string or undefined
      if (!submitData.parent_id || submitData.parent_id === '') {
        submitData.parent_id = null;
      }

      if (editingCategory) {
        await updateMutation.mutateAsync({
          id: editingCategory.id,
          data: submitData,
        });
      } else {
        await createMutation.mutateAsync(submitData);
      }

      // Force refresh the categories data
      setTimeout(() => {
        refetch();
      }, 500);
    } catch (error) {
      console.error('Failed to save category:', error);
    }
  };

  const handleDelete = (id) => {
    deleteMutation.mutate(id);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingCategory(null);
    setSelectedParent(null);
    form.resetFields();
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      render: (text, record) => {
        const hasChildren = record.children && record.children.length > 0;
        const isExpanded = expandedRowKeys.includes(record.id);

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {hasChildren && (
              <span
                style={{
                  fontSize: '14px',
                  color: '#1890ff',
                  marginRight: '4px',
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                {isExpanded ? <DownOutlined /> : <RightOutlined />}
              </span>
            )}
            <strong style={{ fontSize: '18px' }}>{text}</strong>
            {record.children && record.children.length > 0 && (
              <Tag
                color="blue"
                size="small"
                style={{
                  fontSize: '13px',
                  fontWeight: 'normal',
                  opacity: 0.8,
                }}
              >
                {record.children.length} Subcategories
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 250,
      render: (text) => (
        <div
          style={{
            maxWidth: '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            lineHeight: '1.4',
            padding: text ? '6px 8px' : '6px 8px',
            backgroundColor: text ? '#fafafa' : 'transparent',
            borderRadius: '4px',
            border: text ? '1px solid #f0f0f0' : 'none',
            fontStyle: text ? 'normal' : 'italic',
            color: text ? 'inherit' : '#999',
          }}
          title={text || 'No description provided'}
        >
          {text || 'No description'}
        </div>
      ),
    },
    {
      title: 'Products',
      dataIndex: 'products_count',
      key: 'products_count',
      width: 100,
      render: (count) => {
        const productCount = count || 0;
        return (
          <div style={{ padding: '6px 8px' }}>
            <Tag color={productCount > 0 ? 'green' : 'blue'}>{productCount}</Tag>
          </div>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type) => (
        <div style={{ padding: '6px 8px' }}>
          <Tag color={type === 'product' ? 'green' : 'orange'}>
            {type?.toUpperCase() || 'PRODUCT'}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (isActive) => (
        <div style={{ padding: '6px 8px' }}>
          <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'ACTIVE' : 'INACTIVE'}</Tag>
        </div>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => (
        <div style={{ padding: '6px 8px' }}>{date ? new Date(date).toLocaleDateString() : '-'}</div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 280,
      render: (_, record) => (
        <Space>
          {!record.parent_id && (
            <Button
              type="default"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => handleAddSubCategory(record)}
            >
              Add Subcategory
            </Button>
          )}
          <Tooltip title={record.is_active ? 'Deactivate Category' : 'Activate Category'}>
            <Switch
              size="small"
              checked={record.is_active}
              onChange={(checked) => handleToggleStatus(record.id, checked)}
              loading={toggleStatusMutation.isLoading}
            />
          </Tooltip>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this category?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              loading={deleteMutation.isLoading}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (error) {
    return (
      <Alert
        message="Error Loading Categories"
        description="Failed to load categories data. Please try again later."
        type="error"
        showIcon
        action={
          <Button size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }
        style={{ marginBottom: 24 }}
      />
    );
  }

  return (
    <div>
      <Title level={2}>Category Management</Title>
      <style>
        {`
          .category-table .ant-table-expanded-row {
            background-color: #fafafa;
          }
          .category-table .ant-table-expanded-row .ant-table-wrapper {
            margin: 0;
          }
          .subcategory-table .ant-table-tbody > tr > td {
            border-bottom: 1px solid #f0f0f0;
            background-color: #fafafa;
          }
          .subcategory-table .ant-table-tbody > tr:hover > td {
            background-color: #f0f8ff !important;
          }
          .category-table .ant-table-row-expand-icon-cell {
            padding: 8px !important;
          }
          .expand-icon:hover {
            background-color: #e6f7ff !important;
            color: #096dd9 !important;
            transform: scale(1.1);
          }
          .category-table .ant-table-row-indent {
            width: 20px !important;
          }
          .category-table .ant-table-row-expand-icon {
            margin-right: 8px !important;
          }
        `}
      </style>
      <Card>
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalVisible(true)}>
            Add Category
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>
            Refresh
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={categories}
          rowKey="id"
          loading={isLoading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} categories`,
          }}
          scroll={{ x: 1000 }}
          expandable={{
            childrenColumnName: 'children',
            indentSize: 20,
            defaultExpandAllRows: false,
            expandedRowKeys,
            onExpandedRowsChange: (expandedRows) => {
              setExpandedRowKeys(expandedRows);
            },
            expandIconColumnIndex: -1, // Hide the default expand icon column
          }}
          onRow={(record) => ({
            onClick: (event) => {
              // Prevent expand/collapse when clicking on action buttons
              if (
                event.target.closest('button') ||
                event.target.closest('.ant-popconfirm') ||
                event.target.closest('.ant-switch')
              ) {
                return;
              }

              // Only expand if the category has children
              if (record.children && record.children.length > 0) {
                const isExpanded = expandedRowKeys.includes(record.id);
                if (isExpanded) {
                  setExpandedRowKeys(expandedRowKeys.filter((key) => key !== record.id));
                } else {
                  setExpandedRowKeys([...expandedRowKeys, record.id]);
                }
              }
            },
            style: {
              cursor: record.children && record.children.length > 0 ? 'pointer' : 'default',
            },
          })}
          rowClassName={(record, index) => `category-row-${index}`}
          size="middle"
          bordered={false}
          className="category-table"
        />
      </Card>

      <Modal
        title={
          editingCategory
            ? 'Edit Category'
            : selectedParent
              ? 'Create Subcategory'
              : 'Create Category'
        }
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="parent_id"
            label="Parent Category"
            help="Leave empty to make this a main category, or select a parent to make it a subcategory"
          >
            <Select
              placeholder="Select parent category (leave empty for main category)"
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              onChange={(value) => {
                form.setFieldsValue({ parent_id: value });
                setSelectedParent(value);
              }}
              onClear={() => {
                form.setFieldsValue({ parent_id: undefined });
                setSelectedParent(null);
              }}
            >
              {categories
                .filter((cat) => !cat.parent_id && cat.id !== editingCategory?.id)
                .map((category) => (
                  <Option key={category.id} value={category.id}>
                    {category.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="type"
            label="Category Type"
            rules={[{ required: true, message: 'Please select category type!' }]}
          >
            <Select placeholder="Select category type">
              <Option value="product">Product</Option>
              <Option value="service">Service</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="name"
            label="Category Name"
            rules={[{ required: true, message: 'Please input category name!' }]}
          >
            <Input placeholder="Enter category name" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please input description!' }]}
          >
            <Input.TextArea rows={4} placeholder="Enter category description" />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Status"
            rules={[{ required: true, message: 'Please select status!' }]}
          >
            <Select placeholder="Select status">
              <Option value={true}>Active</Option>
              <Option value={false}>Inactive</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createMutation.isLoading || updateMutation.isLoading}
              >
                {editingCategory ? 'Update Category' : 'Create Category'}
              </Button>
              <Button onClick={handleCancel}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Categories;
